import { useScrollAnimation } from '@/hooks/useScrollAnimation';
import { useSiteData, type VehicleLocation } from '@/data/siteData';

const parks: Array<{
  name: string;
  location: VehicleLocation;
  directionsLabel: string;
  addressLine1: string;
  addressLine2: string;
  directionsUrl: string;
}> = [
  {
    name: 'PARC DU TAMPON',
    location: 'Le Tampon',
    directionsLabel: 'Parc du Tampon',
    addressLine1: '57 Rue du Général de Gaulle',
    addressLine2: '97430 Le Tampon',
    directionsUrl: 'https://www.google.com/maps/dir//57+Rue+du+Général+de+Gaulle,+97430+Le+Tampon',
  },
  {
    name: 'PARC DE SAINT-PIERRE',
    location: 'Saint-Pierre',
    directionsLabel: 'Parc de Saint-Pierre',
    addressLine1: '153 Route de la Ligne Paradis',
    addressLine2: '97410 Saint-Pierre',
    directionsUrl: 'https://www.google.com/maps/dir//153+Route+de+la+Ligne+Paradis,+97410+Saint-Pierre',
  },
];

export default function ContactSection() {
  const { ref, isVisible } = useScrollAnimation(0.1);
  const { hours } = useSiteData();

  return (
    <section id="contact" className="py-16 md:py-24 bg-background-100">
      <div className="w-full px-4 md:px-6 lg:px-10">
        <div ref={ref} className={`text-center transition-all duration-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <h2 className="font-heading text-2xl md:text-3xl lg:text-4xl font-bold text-foreground-900">Contactez-nous</h2>
          <p className="text-foreground-600 mt-3 max-w-xl mx-auto text-sm md:text-base">
            Easy Car vous accueille dans ses deux parcs à La Réunion. Même équipe, même numéro, même exigence.
          </p>
        </div>

        <div
          className={`bg-white rounded-lg border border-background-200 p-6 md:p-8 mt-8 md:mt-12 transition-all duration-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
          style={{ transitionDelay: '100ms' }}
        >
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-10">
            <div className="flex flex-col justify-center">
              <h3 className="font-heading font-bold text-lg text-foreground-900 mb-4">Coordonnées</h3>
              <div className="space-y-3">
                <a href="tel:+262692505551" className="flex items-center gap-3 text-foreground-700 text-sm hover:text-primary-600 transition-colors">
                  <i className="ri-phone-line w-5 h-5 flex items-center justify-center text-primary-500 shrink-0" />
                  0692 50 55 51
                </a>
                <a href="mailto:easycar974@gmail.com" className="flex items-center gap-3 text-foreground-700 text-sm hover:text-primary-600 transition-colors">
                  <i className="ri-mail-line w-5 h-5 flex items-center justify-center text-primary-500 shrink-0" />
                  easycar974@gmail.com
                </a>
              </div>

              <div className="mt-6 flex flex-wrap items-center gap-3">
                <a href="tel:+262692505551" className="inline-flex items-center gap-2 px-4 py-2.5 rounded-md bg-primary-500 text-white font-label font-medium text-sm hover:bg-primary-600 transition-all whitespace-nowrap">
                  <i className="ri-phone-line w-4 h-4 flex items-center justify-center" /> Appelez-nous
                </a>
                <a href="https://wa.me/262692505551" target="_blank" rel="noopener noreferrer" aria-label="Écrivez-nous sur WhatsApp" title="Écrivez-nous sur WhatsApp" className="inline-flex items-center gap-2 px-4 py-2.5 rounded-md bg-green-500 text-white font-label font-medium text-sm hover:bg-green-600 transition-all whitespace-nowrap">
                  <i className="ri-whatsapp-line w-4 h-4 flex items-center justify-center text-base" /> Écrivez-nous
                </a>
              </div>
            </div>

            <div>
              <h3 className="font-heading font-bold text-lg text-foreground-900 mb-4">Horaires</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                {(['Le Tampon', 'Saint-Pierre'] as VehicleLocation[]).map((location) => (
                  <div key={location}>
                    <h4 className="font-heading font-semibold text-sm text-foreground-900 mb-2">{location}</h4>
                    <div className="space-y-1.5 text-xs md:text-sm">
                      {hours[location].map((h) => (
                        <div key={h.day} className="flex items-center justify-between gap-3">
                          <span className="text-foreground-600">{h.day}</span>
                          <span className={h.time === 'Fermé' ? 'text-foreground-400' : 'text-foreground-700'}>{h.time}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 md:gap-10 mt-8 md:mt-10">
          {parks.map((park, idx) => (
            <div key={park.name} className={`bg-white rounded-lg border border-background-200 p-6 md:p-8 flex flex-col transition-all duration-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`} style={{ transitionDelay: `${200 + idx * 100}ms` }}>
              <span className="inline-flex items-center gap-2 text-primary-700 bg-primary-100 px-3 py-1 rounded-full text-xs font-label font-medium self-start">
                <i className="ri-map-pin-line w-3.5 h-3.5 flex items-center justify-center" /> Easy Car
              </span>
              <h3 className="font-heading font-bold text-lg md:text-xl text-foreground-900 mt-3">{park.name}</h3>
              <div className="space-y-3 mt-4">
                <div className="flex items-start gap-3">
                  <i className="ri-map-pin-2-line w-5 h-5 flex items-center justify-center text-primary-500 mt-0.5 shrink-0" />
                  <span className="text-foreground-700 text-sm">{park.addressLine1}<br />{park.addressLine2}<br />La Réunion</span>
                </div>
                <a href="tel:+262692505551" className="flex items-center gap-3 text-foreground-700 text-sm hover:text-primary-600 transition-colors">
                  <i className="ri-phone-line w-5 h-5 flex items-center justify-center text-primary-500 shrink-0" /> 0692 50 55 51
                </a>
              </div>
              <div className="mt-6 pt-6 border-t border-background-200">
                <a href={park.directionsUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 px-4 py-2.5 rounded-md border border-foreground-300 text-foreground-700 font-label font-medium text-sm hover:border-primary-500 hover:text-primary-600 transition-all whitespace-nowrap">
                  <i className="ri-route-line w-4 h-4 flex items-center justify-center" /> Itinéraire — {park.directionsLabel}
                </a>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
